'use client';

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { Box } from '@highlight-cp/core-ui';

import classes from './animated-height.module.css';

/** How long after a window resize to keep treating height changes as reflow, not content. */
const RESIZE_QUIET_MS = 250;

export type HeightChangePhase = 'start' | 'settle';

export interface AnimatedHeightProps {
  /** Content whose size changes drive the height transition */
  children: ReactNode;
  onHeightChange?: (phase: HeightChangePhase) => void;
}

/**
 * Transitions its own height whenever the content inside it changes size, so
 * conditionally mounted children grow and shrink the surrounding card smoothly
 * instead of snapping in a single frame.
 *
 * Use it when content mounts and unmounts. When content is always present and only
 * toggles visibility, reach for core-ui `Collapse` instead.
 *
 * The content sits in an inner box that is never height-constrained, so it always
 * reports its natural height and the observer can't feed on its own animation. That
 * box establishes no block formatting context, so a child's vertical margin collapses
 * through it and is left out of the measurement — compose with `Stack`/`gap` rather
 * than margins. While a transition runs the wrapper clips its overflow, so a
 * non-portalled floating element inside it would be hidden for the duration.
 */
export const AnimatedHeight = ({ children, onHeightChange }: AnimatedHeightProps) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const onHeightChangeRef = useRef(onHeightChange);
  useEffect(() => {
    onHeightChangeRef.current = onHeightChange;
  });

  useEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;

    // Reduced motion is handled here rather than in CSS on purpose: a `transition: none`
    // rule would cancel an in-flight transition without ever firing `transitionend`.
    // Never pinning a height means there is nothing to cancel.
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    let previousHeight = content.getBoundingClientRect().height;
    let settleTimer = 0;

    // Chasing the height while the window is being dragged would clip the content on
    // every frame of the drag. This keys off the resize event rather than comparing
    // widths, because content that grows tall enough to summon a scrollbar also shrinks
    // its own width — and that reveal is exactly the one worth animating.
    let lastResizeAt = -RESIZE_QUIET_MS;
    const onResize = () => {
      lastResizeAt = performance.now();
    };
    window.addEventListener('resize', onResize);

    // Back to auto, so wrapping and window resizes reflow normally and nothing is clipped.
    const settle = () => {
      window.clearTimeout(settleTimer);
      viewport.style.height = '';
      viewport.style.overflow = '';
      onHeightChangeRef.current?.('settle');
    };

    const observer = new ResizeObserver(() => {
      const to = content.getBoundingClientRect().height;
      const grewOrShrank = Math.abs(previousHeight - to) >= 1;
      const resizing = performance.now() - lastResizeAt < RESIZE_QUIET_MS;
      const from = previousHeight;
      previousHeight = to;

      if (!grewOrShrank) return;

      if (resizing || reducedMotion.matches) {
        onHeightChangeRef.current?.('start');
        onHeightChangeRef.current?.('settle');
        return;
      }

      onHeightChangeRef.current?.('start');

      // Observer callbacks run after layout, so an un-pinned viewport has *already*
      // reflowed to the new height — the last measurement is the only record of where it
      // came from. Mid-transition it carries an explicit height instead, so read that
      // live and continue from where the interrupted transition got to.
      const start = viewport.style.height ? viewport.getBoundingClientRect().height : from;

      viewport.style.overflow = 'hidden';
      viewport.style.height = `${start}px`;
      viewport.getBoundingClientRect(); // flush the start height so the transition has two endpoints
      viewport.style.height = `${to}px`;

      // `transitionend` never arrives if the transition doesn't run or gets cancelled — a
      // backgrounded tab, an ancestor going `display: none` — which would strand the
      // viewport clipped at a pinned height. Read the duration off the element so this
      // can't drift from the CSS.
      const durationMs = parseFloat(getComputedStyle(viewport).transitionDuration) * 1000;
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settle, durationMs + 50);
    });
    observer.observe(content);

    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== viewport || event.propertyName !== 'height') return;
      settle();
    };
    // Focus can land on revealed content before the transition finishes — end it early
    // rather than leave the focused control clipped by the pinned height.
    const onFocusIn = () => {
      if (viewport.style.height) settle();
    };
    viewport.addEventListener('transitionend', onTransitionEnd);
    viewport.addEventListener('focusin', onFocusIn);

    return () => {
      window.clearTimeout(settleTimer);
      window.removeEventListener('resize', onResize);
      observer.disconnect();
      viewport.removeEventListener('transitionend', onTransitionEnd);
      viewport.removeEventListener('focusin', onFocusIn);
    };
  }, []);

  return (
    <Box ref={viewportRef} className={classes.viewport}>
      <Box ref={contentRef}>{children}</Box>
    </Box>
  );
};
